import { Controller } from "@hotwired/stimulus"

// Live "total" line on the trade entry form (PRD §2.5): quantity x price, with
// brokerage added for an acquisition and subtracted for a disposal.
//
// It is a PREVIEW, never an input. Nothing here is submitted and nothing is
// stored from it — the server recomputes the same figure from the same three
// fields (PortfolioTrade#cost_base_cents / #net_proceeds_cents). That matters:
// a total posted by the client would be a fourth number that can disagree with
// the three it is derived from, and the one the user checked is not the one the
// cost base would have been built from.
//
// Which kinds are disposals is supplied by the server rather than hardcoded
// here — PortfolioTrade::BEHAVIOUR_BY_KIND is the single home for that fact,
// and a copy in JS would go stale the moment C21 adds a kind.
//
//   data-controller="trade-total"
//   data-trade-total-disposing-kinds-value='["sell","buyback"]'
//   targets: kind, quantity, price, fees, total, caption
export default class extends Controller {
  static targets = ["kind", "quantity", "price", "fees", "total", "caption"]
  static values = { disposingKinds: { type: Array, default: [] } }

  connect() {
    this.recalculate()
  }

  recalculate() {
    // Guarded like every other target here: a missing target throws inside
    // connect(), Stimulus swallows it, and the preview then simply never
    // appears with nothing in the console to say why.
    const quantity = this.hasQuantityTarget ? this.number(this.quantityTarget) : null
    const price = this.hasPriceTarget ? this.number(this.priceTarget) : null
    const fees = this.hasFeesTarget ? (this.number(this.feesTarget) ?? 0) : 0
    const disposing = this.isDisposing()

    // An incomplete form shows a dash, not "$0.00". A zero total reads as a
    // real answer, and the user would have no way to tell it apart from a
    // genuinely free trade.
    // Read the caption even on an incomplete form: a half-filled sell that
    // still says "Total cost" tells the user the wrong thing about a form they
    // are in the middle of.
    if (quantity === null || price === null) {
      this.render(null, disposing)
      return
    }

    const consideration = quantity * price

    this.render(disposing ? consideration - fees : consideration + fees, disposing)
  }

  isDisposing() {
    if (!this.hasKindTarget) return false

    // Array-checked rather than trusted: this runs from connect(), and a value
    // that has not been bound yet would throw there — where Stimulus swallows
    // the error and the preview would simply never appear, with nothing in the
    // console to say why. Falling back to "not a disposal" also matches the
    // rule for a kind the server did not list.
    const kinds = this.disposingKindsValue
    return Array.isArray(kinds) && kinds.includes(this.kindTarget.value)
  }

  render(total, disposing) {
    if (this.hasCaptionTarget) {
      this.captionTarget.textContent = disposing ? "Net proceeds" : "Total cost"
    }

    if (!this.hasTotalTarget) return

    this.totalTarget.textContent = total === null ? "—" : this.format(total)
  }

  format(amount) {
    try {
      // Single-currency app (Plan::PRICES is AUD); nothing configurable here.
      return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(amount)
    } catch {
      // A locale or currency the browser rejects must not take the whole form
      // down — the preview degrades, the form still submits.
      return `$${amount.toFixed(2)}`
    }
  }

  // null (rather than 0) for blank or non-numeric input, so "not filled in yet"
  // and "zero" stay distinguishable all the way to render().
  number(element) {
    if (!element || element.value.trim() === "") return null

    const parsed = Number(element.value)
    return Number.isFinite(parsed) ? parsed : null
  }
}
